import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

function formatPrice(n) {
  return Number(n || 0).toLocaleString('ko-KR');
}

function buildImageUrl(path) {
  const raw = String(path || '').trim();

  if (!raw) {
    return '/static/default_thumb.jpg';
  }

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  const cleaned = raw
    .replace(/\\/g, '/')
    .replace(/\.\.\//g, '')
    .replace(/\.\//g, '')
    .replace(/^\/+/, '');

  return `/static/${cleaned}`;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function getOriginalPrice(product) {
  return Number(
    product?.original_price ??
    product?.normal_price ??
    product?.price ??
    0
  );
}

function getDisplayPrice(product) {
  return Number(
    product?.display_price ??
    product?.sale_price ??
    product?.price ??
    0
  );
}

export default function TableOrder() {
  const query = useQuery();
  const navigate = useNavigate();

  const shopId = Number(query.get('shop_id') || 0);
  const tableId = Number(query.get('table_id') || 0);
  const categoryIdFromUrl = Number(query.get('id') || 0);
  const entry = query.get('entry') || '';

  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState(0);

  const [shop, setShop] = useState(null);
  const [table, setTable] = useState(null);
  const [cats, setCats] = useState([]);
  const [categoryId, setCategoryId] = useState(categoryIdFromUrl);
  const [products, setProducts] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [token, setToken] = useState('');

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('장바구니에 담겼습니다.');

  const [optionModal, setOptionModal] = useState({
    open: false,
    product: null,
    selected: {},
  });

  const entryQs = entry ? `&entry=${encodeURIComponent(entry)}` : '';

  const pageTitle = useMemo(() => {
    const shopName = shop?.name || '테이블 주문';
    return `${shopName} 테이블 주문`;
  }, [shop]);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => {
    let ignore = false;

    async function loadPageData() {
      setLoading(true);
      try {
        const res = await axios.get('/api/table/order-data', {
          params: {
            shop_id: shopId,
            table_id: tableId,
            id: categoryIdFromUrl || undefined,
            entry: entry || undefined,
          },
          withCredentials: true,
        });

        if (ignore) return;

        const data = res.data || {};
        setShop(data.shop || null);
        setTable(data.table || null);
        setCats(Array.isArray(data.cats) ? data.cats : []);
        setCategoryId(Number(data.category_id || 0));
        setProducts(Array.isArray(data.products) ? data.products : []);
        setCartCount(Number(data.cart_count || 0));
        setToken(String(data.token || ''));

        if (data.redirect_category_id && !categoryIdFromUrl) {
          navigate(
            `/table/order?shop_id=${shopId}&table_id=${tableId}&id=${Number(data.redirect_category_id)}${entryQs}`,
            { replace: true }
          );
        }
      } catch (err) {
        console.error('테이블 주문 데이터 조회 실패:', err);
        alert(err.response?.data?.message || '테이블 주문 데이터를 불러오지 못했습니다.');
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    if (shopId > 0 && tableId > 0) {
      loadPageData();
    } else {
      setLoading(false);
      alert('잘못된 접근입니다.');
    }

    return () => {
      ignore = true;
    };
  }, [shopId, tableId, categoryIdFromUrl, entry, entryQs, navigate]);

  const handleCategoryClick = (cid) => {
    navigate(`/table/order?shop_id=${shopId}&table_id=${tableId}&id=${cid}${entryQs}`);
  };

  const submitProduct = async (product, selectedOptionItems = []) => {
    if (!product?.id) return;

    setSubmittingId(Number(product.id));
    try {
      const formData = new FormData();
      formData.append('product_id', String(product.id));
      formData.append('added_name', String(product.name || ''));
      formData.append('selected_option_items', JSON.stringify(selectedOptionItems));

      const res = await axios.post('/api/table/add', formData, {
        params: {
          shop_id: shopId,
          table_id: tableId,
          id: categoryId || undefined,
          entry: entry || undefined,
        },
        withCredentials: true,
      });

      const data = res.data || {};
      if (!data.success) {
        alert(data.message || '장바구니 담기 실패');
        return;
      }

      setCartCount(Number(data.cart_count || 0));
      setToken(String(data.token || token || ''));
      setToastMsg(data.added_name ? `담김: ${data.added_name}` : '장바구니에 담겼습니다.');
      setToastOpen(true);
      setOptionModal({ open: false, product: null, selected: {} });
      window.setTimeout(() => setToastOpen(false), 700);
    } catch (err) {
      console.error('상품 담기 실패:', err);
      alert(err.response?.data?.message || '장바구니 담기 실패');
    } finally {
      setSubmittingId(0);
    }
  };

  const handleAddProduct = (product) => {
    if (!product?.id) return;

    const groups = Array.isArray(product?.option_groups)
      ? product.option_groups
      : [];

    if (groups.length === 0) {
      submitProduct(product, []);
      return;
    }

    setOptionModal({
      open: true,
      product,
      selected: {},
    });
  };

  const handleOptionChange = (groupId, itemId) => {
    setOptionModal((prev) => ({
      ...prev,
      selected: {
        ...prev.selected,
        [String(groupId)]: Number(itemId || 0),
      },
    }));
  };

  const confirmOptionProduct = () => {
    const product = optionModal.product;
    if (!product) return;

    const groups = Array.isArray(product.option_groups)
      ? product.option_groups
      : [];

    const selectedOptionItems = [];

    for (const group of groups) {
      const selectedItemId = Number(
        optionModal.selected[String(group.id)] || 0
      );

      const required =
        String(group.required || 'N').toUpperCase() === 'Y';

      if (required && selectedItemId <= 0) {
        alert(`${group.group_name || '필수 옵션'}을 선택해주세요.`);
        return;
      }

      if (selectedItemId <= 0) continue;

      const item = (Array.isArray(group.items) ? group.items : []).find(
        (row) => Number(row.id) === selectedItemId
      );

      if (!item) continue;

      selectedOptionItems.push({
        group_id: Number(group.id),
        item_id: Number(item.id),
        group_name: group.group_name || '',
        item_name: item.item_name || '',
        extra_price: Number(item.extra_price || 0),
      });
    }

    submitProduct(product, selectedOptionItems);
  };

  const selectedOptionTotal = useMemo(() => {
    const product = optionModal.product;
    if (!product) return 0;

    const groups = Array.isArray(product.option_groups)
      ? product.option_groups
      : [];

    return groups.reduce((sum, group) => {
      const selectedItemId = Number(
        optionModal.selected[String(group.id)] || 0
      );

      const item = (Array.isArray(group.items) ? group.items : []).find(
        (row) => Number(row.id) === selectedItemId
      );

      return sum + Number(item?.extra_price || 0);
    }, 0);
  }, [optionModal]);

  const handleClearCart = async () => {
    if (!window.confirm('장바구니를 전부 비울까요?')) return;

    try {
      const formData = new FormData();
      formData.append('action', 'clear');

      const res = await axios.post('/api/pos/table/cart', formData, {
        params: {
          shop_id: shopId,
          table_id: tableId,
          entry: entry || undefined,
        },
        withCredentials: true,
      });

      const data = res.data || {};
      if (!data.success) {
        alert(data.message || '초기화 실패');
        return;
      }

      setCartCount(0);
    } catch (err) {
      console.error('장바구니 초기화 실패:', err);
      alert(err.response?.data?.message || '초기화 실패');
    }
  };

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}></div>;
  }

  return (
    <>
      <style>{`
        body { margin-bottom: 90px; }
        .table-order-topbar {
          position: fixed;
          inset: 0 0 auto 0;
          height: 60px;
          background: #fff;
          border-bottom: 0px solid #eee;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 14px;
          z-index: 1000;
        }
        .table-order-wrap { padding-top: 76px; }
        .table-order-cat-row {
          overflow-x: auto;
          white-space: nowrap;
          -webkit-overflow-scrolling: touch;

          /* 스크롤은 가능하지만 스크롤바는 숨김 */
          scrollbar-width: none;      /* Firefox */
          -ms-overflow-style: none;   /* IE, Edge */
        }

        .table-order-cat-row::-webkit-scrollbar {
          display: none;              /* Chrome, Safari */
          width: 0;
          height: 0;
        }
        .table-order-cat-btn {
          display: inline-block;
          padding: 8px 10px;
          margin-right: 6px;
          border-radius: 999px;
          text-decoration: none;
          color: #111;
          border: 1px solid #e9ecef;
          font-weight: 700;
          font-size: 14px;
          background: #fff;
        }
        .table-order-cat-btn.active {
          background: #111;
          color: #fff;
          border-color: #111;
        }
        .table-order-checkout-bar {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          background: #fff;
          border-top: 0px solid #eee;
          padding: 10px;
          z-index: 1000;
        }
        .table-order-toast-overlay {
          display: ${toastOpen ? 'block' : 'none'};
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.35);
          z-index: 2400;
        }
        .table-order-toast-container {
          position: fixed;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: 2500;
          display: ${toastOpen ? 'block' : 'none'};
        }
        .table-order-toast-box {
          background: #212529;
          color: #fff;
          border-radius: 10px;
          min-width: 220px;
          padding: 14px 16px;
          font-size: 14px;
          box-shadow: 0 10px 30px rgba(0,0,0,.2);
        }
        .table-order-card {
          border-radius: 0 !important;
          overflow: visible;
          box-shadow: none !important;
          margin-bottom: 15px;
          border: 0;
          background: #fff;
        }
        .table-order-card img {
          height: auto;
          max-height: 220px;
          object-fit: cover;
          background: #f1f3f5;
          border-radius: 0 !important;
          width: 100%;
          display: block;
        }
        .table-order-card-btn {
          width: 100%;
          padding: 0;
          border: 0;
          background: transparent;
          text-align: left;
        }
        .table-order-card-btn:active {
          transform: scale(0.99);
        }
        .table-order-image-wrap { position: relative; }
        .table-order-add-overlay {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: rgba(0,0,0,0.45);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 800;
          pointer-events: none;
          user-select: none;
        }
        .table-order-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 10px 6px;
        }
        .table-order-name {
          font-weight: 800;
          font-size: 0.95rem;
          margin: 0;
          line-height: 1.2;
          color: #222;
        }
        .table-order-price {
          font-weight: 800;
          font-size: 0.95rem;
          margin: 0;
          line-height: 1.2;
          color: #666;
        }
        .table-order-original-price {
          color: #999;
          font-size: 0.8rem;
          text-decoration: line-through;
          line-height: 1.2;
        }
        .table-order-sale-price {
          color:rgb(0, 0, 0);
          font-weight: 800;
          font-size: 0.95rem;
          line-height: 1.2;
        }
        .table-order-option-label {
          color:rgb(144, 144, 144);
          font-size: 0.8rem;
          font-weight: 700;
        }
        .table-order-no-image {
          width: 100%;
          max-height: 220px;
          background: #f1f3f5;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c757d;
          font-size: 12px;
          aspect-ratio: 4/3;
        }
        .table-order-option-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.45);
          z-index: 3000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
        }
        .table-order-option-modal {
          width: 100%;
          max-width: 480px;
          max-height: 85vh;
          overflow-y: auto;
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 18px 50px rgba(0,0,0,.25);
        }
        .table-order-option-header {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #fff;
          border-bottom: 1px solid #eee;
          padding: 16px;
        }
        .table-order-option-body {
          padding: 16px;
        }
        .table-order-option-group {
          padding-bottom: 16px;
          margin-bottom: 16px;
          border-bottom: 1px solid #eee;
        }
        .table-order-option-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 11px 12px;
          border: 1px solid #e9ecef;
          border-radius: 10px;
          margin-top: 8px;
          cursor: pointer;
        }
        .table-order-option-footer {
          position: sticky;
          bottom: 0;
          background: #fff;
          border-top: 1px solid #eee;
          padding: 12px;
        }
      `}</style>

      <div className="table-order-topbar">
        <div>
          <h4 className="fw-bold m-0">{shop?.name || ''} · {table?.name || ''}</h4>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-dark btn-sm position-relative"
            onClick={() => navigate(`/table/cart?shop_id=${shopId}&table_id=${tableId}${entryQs}`)}
          >
            장바구니
            {cartCount > 0 && <span className="badge bg-danger ms-1">{cartCount}</span>}
          </button>
        </div>
      </div>

      <div className="table-order-toast-overlay" />
      <div className="table-order-toast-container">
        <div className="table-order-toast-box">{toastMsg}</div>
      </div>

      {optionModal.open && optionModal.product ? (
        <div
          className="table-order-option-backdrop"
          onClick={() => setOptionModal({ open: false, product: null, selected: {} })}
        >
          <div
            className="table-order-option-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="table-order-option-header">
              <div className="d-flex justify-content-between align-items-start gap-3">
                <div>
                  <div className="fw-bold fs-5">
                    {optionModal.product.name}
                  </div>
                  <div className="small text-muted mt-1">
                    옵션을 선택해주세요.
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="닫기"
                  onClick={() => setOptionModal({ open: false, product: null, selected: {} })}
                />
              </div>
            </div>

            <div className="table-order-option-body">
              {(optionModal.product.option_groups || []).map((group) => {
                const required = String(group.required || 'N').toUpperCase() === 'Y';
                const selectedItemId = Number(
                  optionModal.selected[String(group.id)] || 0
                );

                return (
                  <div className="table-order-option-group" key={group.id}>
                    <div className="fw-bold">
                      {group.group_name || '옵션'}
                      {required ? <span className="text-danger ms-1">*</span> : null}
                    </div>

                    {!required ? (
                      <label className="table-order-option-item">
                        <span>선택 안 함</span>
                        <input
                          type="radio"
                          name={`option-group-${group.id}`}
                          checked={selectedItemId === 0}
                          onChange={() => handleOptionChange(group.id, 0)}
                        />
                      </label>
                    ) : null}

                    {(Array.isArray(group.items) ? group.items : []).map((item) => (
                      <label className="table-order-option-item" key={item.id}>
                        <span>{item.item_name}</span>
                        <span className="d-flex align-items-center gap-2">
                          {Number(item.extra_price || 0) !== 0 ? (
                            <span className="small text-muted">
                              {Number(item.extra_price || 0) > 0 ? '+' : ''}
                              {formatPrice(item.extra_price)}원
                            </span>
                          ) : null}
                          <input
                            type="radio"
                            name={`option-group-${group.id}`}
                            checked={selectedItemId === Number(item.id)}
                            onChange={() => handleOptionChange(group.id, item.id)}
                          />
                        </span>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>

            <div className="table-order-option-footer">
              <button
                type="button"
                className="btn btn-dark w-100 py-3"
                disabled={submittingId === Number(optionModal.product.id)}
                onClick={confirmOptionProduct}
              >
                {submittingId === Number(optionModal.product.id)
                  ? '담는 중...'
                  : `${formatPrice(
                      getDisplayPrice(optionModal.product) + selectedOptionTotal
                    )}원 담기`}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="container table-order-wrap">
        <div className="table-order-cat-row mb-3">
          {cats.map((c) => {
            const cid = Number(c.id || 0);
            const active = cid === Number(categoryId || 0);
            return (
              <button
                key={cid}
                type="button"
                className={`table-order-cat-btn ${active ? 'active' : ''}`}
                onClick={() => handleCategoryClick(cid)}
              >
                {c.name}
              </button>
            );
          })}
        </div>

        <div className="row row-cols-2 row-cols-md-4 g-3 g-md-4">
          {products.map((p) => {
            const imageUrl = buildImageUrl(p.image_path);
            const isSubmitting = submittingId === Number(p.id);
            const originalPrice = getOriginalPrice(p);
            const displayPrice = getDisplayPrice(p);
            const isSale = Boolean(p.is_sale) || (
              originalPrice > 0 &&
              displayPrice > 0 &&
              displayPrice < originalPrice
            );
            const hasOptions =
              Array.isArray(p.option_groups) &&
              p.option_groups.length > 0;

            return (
              <div className="col" key={p.id}>
                <button
                  className="table-order-card-btn"
                  type="button"
                  onClick={() => handleAddProduct(p)}
                  disabled={isSubmitting}
                >
                  <div className="card table-order-card h-100">
                    <div className="table-order-image-wrap">
                      <img
                        className="card-img-top"
                        src={imageUrl}
                        alt=""
                        onError={(e) => {
                          e.currentTarget.src = '/static/default_thumb.jpg';
                        }}
                      />
                      <div className="table-order-add-overlay">
                        {isSubmitting ? '…' : '+'}
                      </div>
                    </div>

                    <div className="table-order-info">
                      <div className="table-order-name">{p.name}</div>

                      {isSale ? (
                        <>
                          <div className="table-order-original-price">
                            {formatPrice(originalPrice)}원
                          </div>
                          <div className="table-order-sale-price">
                            {formatPrice(displayPrice)}원
                          </div>
                        </>
                      ) : (
                        <div className="table-order-price">
                          {formatPrice(displayPrice)}원
                        </div>
                      )}

                      {hasOptions ? (
                        <div className="table-order-option-label">
                          옵션 선택
                        </div>
                      ) : null}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="table-order-checkout-bar">
        <div className="d-flex gap-2">
          <button className="btn btn-outline-dark w-25 text-nowrap" type="button" onClick={handleClearCart}>
            처음부터
          </button>

          <button
            className="btn btn-dark w-75 py-2"
            type="button"
            onClick={() => navigate(`/table/cart?shop_id=${shopId}&table_id=${tableId}${entryQs}`)}
          >
            주문하기
            {cartCount > 0 && (
              <span className="badge bg-light text-dark ms-2">{cartCount}개</span>
            )}
          </button>
        </div>
      </div>
    </>
  );
}